import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { getMoodleFile } from "../api/fileApi";
import { replaceImageSrcInHtml, isPluginFileUrl } from "../common/ults/domParser";

const resolveMoodleFileToUrl = (file: unknown): string | undefined => {
  if (!file) return undefined;

  try {
    if (file instanceof Blob) {
      return URL.createObjectURL(file);
    }

    if (file instanceof ArrayBuffer) {
      const blob = new Blob([file], { type: "image/jpeg" });
      return URL.createObjectURL(blob);
    }

    if (typeof file === "string") {
      return file;
    }

    return undefined;
  } catch (error) {
    console.error("Error processing image:", error);
    return undefined;
  }
};

export const useHandleFile = (contentHash?: any) => {

  const { data: moodleFileSrc, isLoading: isMoodleFileSrcLoading } = useQuery({
    queryKey: ["moodle-file", contentHash],
    queryFn: () => getMoodleFile(contentHash),
    enabled: !!contentHash,
    refetchOnMount: false,
    staleTime: 60000,
    select: resolveMoodleFileToUrl,
  });
  

  return {
    moodleFileSrc,
    isMoodleFileSrcLoading,
  };
};

export const useResolveQuizHtml = (
  html?: string | null,
  imageSrcs: string[] = []
) => {
  const pluginFileSrcs = imageSrcs.filter(isPluginFileUrl);

  const imageQueries = useQueries({
    queries: pluginFileSrcs.map((src) => ({
      queryKey: ["moodle-file", src],
      queryFn: () => getMoodleFile(src),
      enabled: !!src,
      refetchOnMount: false,
      staleTime: 60000,
      select: resolveMoodleFileToUrl,
    })),
  });

  const resolvedUrls = imageQueries.map((query) => query.data);
  const isResolvingImages = pluginFileSrcs.length
    ? imageQueries.some((query) => query.isLoading)
    : false;

  const resolvedHtml = useMemo(() => {
    if (!html) return html || "";

    if (!pluginFileSrcs.length) return html;

    const srcMap: Record<string, string> = {};
    pluginFileSrcs.forEach((src, index) => {
      const resolvedSrc = resolvedUrls[index];
      if (resolvedSrc) {
        srcMap[src] = resolvedSrc;
      }
    });

    return replaceImageSrcInHtml(html, srcMap);
  }, [html, pluginFileSrcs, resolvedUrls]);

  return {
    resolvedHtml,
    isResolvingImages,
  };
};
